import { getCargoMeta } from './painel-meta.js';
import { DEFAULT_PARTY_CATALOG } from './painel-state.js';

const FALLBACK_PARTY_LABEL = 'Sem partido informado';
const FALLBACK_FEDERATION_LABEL = 'Sem federação/coligação informada';

export function aggregateAll(dados, options = {}) {
  const aggregateOptions = typeof options === 'string' ? { cargoKey: options } : (options || {});
  const cargoKey = aggregateOptions.cargoKey ? normalizeCargoKey(aggregateOptions.cargoKey) : '';
  const partidosCatalog = aggregateOptions.partidosCatalog || DEFAULT_PARTY_CATALOG;
  let totalEleitores = 0;
  let totalSecoes = 0;
  let totalVotosValidos = 0;
  let totalVotosNominais = 0;
  let totalAbstencoes = 0;
  let totalVotosLegenda = 0;
  let votosLegendaSecoesComDado = 0;
  let totalBrancos = 0;
  let totalNulos = 0;
  let totalEleitoresSecoes = 0;
  let abstencoesSecoesComDado = 0;
  let abstencoesDisponiveis = false;
  let brancosNulosDisponiveis = false;
  let votosLegendaDisponiveis = false;
  const candidatos = {};
  const genero = { Feminino: 0, Masculino: 0, 'Não Informado': 0 };
  const idade = { '16 a 24 anos': 0, '25 a 49 anos': 0, '50 a 69 anos': 0, '70 anos ou mais': 0, 'Não Informado': 0 };
  const instrucao = {};
  const instrucaoKeys = ['Analfabeto', 'Lê e Escreve', 'Fund. Incompleto', 'Fund. Completo',
    'Médio Incompleto', 'Médio Completo', 'Sup. Incompleto', 'Sup. Completo', 'Não Informado'];
  instrucaoKeys.forEach(key => {
    instrucao[key] = 0;
  });

  const secoesObj = dados?.secoes || dados || {};
  const candidatosRef = dados?.candidatos || {};
  const officialCargoSummary = cargoKey ? getOfficialCargoSummary(dados, cargoKey) : null;
  const fontesMetadados = {};
  const secoes = Object.entries(secoesObj);
  totalSecoes = secoes.length;
  const municipioTotalEleitores = toOptionalNumber(dados?.municipio_total_aptos);

  if (municipioTotalEleitores !== null) {
    totalEleitores = municipioTotalEleitores;
  }

  secoes.forEach(([key, sec]) => {
    const aptos = toOptionalNumber(sec?.aptos);
    if (aptos !== null) {
      totalEleitoresSecoes += aptos;
      if (municipioTotalEleitores === null) totalEleitores += aptos;
    }

    const abstencoes = cargoKey
      ? getCargoScopedOptionalNumber(sec, cargoKey, ['abstencoes', 'abstencao'])
      : toOptionalNumber(sec?.abstencoes);
    if (abstencoes !== null) {
      abstencoesDisponiveis = true;
      abstencoesSecoesComDado += 1;
      totalAbstencoes += abstencoes;
      fontesMetadados.abstencoes = {
        fonte: cargoKey ? `secao.abstencoes_${cargoKey}` : 'secao.abstencoes',
        granularidade: 'secao',
        usadoNoRecorte: true,
        regra: 'Soma abstenções presentes nas seções do recorte.',
      };
    }

    const brancos = cargoKey
      ? getCargoScopedOptionalNumber(sec, cargoKey, ['brancos', 'votos_brancos', 'total_brancos'])
      : getOptionalNumberByAliases(sec, ['brancos', 'votos_brancos', 'total_brancos']);
    const nulos = cargoKey
      ? getCargoScopedOptionalNumber(sec, cargoKey, ['nulos', 'votos_nulos', 'total_nulos'])
      : getOptionalNumberByAliases(sec, ['nulos', 'votos_nulos', 'total_nulos']);
    if (brancos !== null || nulos !== null) {
      brancosNulosDisponiveis = true;
      totalBrancos += brancos || 0;
      totalNulos += nulos || 0;
    }

    if (cargoKey) {
      const votosLegenda = getCargoLegendVotes(sec, cargoKey);
      if (votosLegenda !== null) {
        votosLegendaDisponiveis = true;
        votosLegendaSecoesComDado += 1;
        totalVotosLegenda += votosLegenda;
      }
    }

    accumulateProfile(genero, sec?.perfil_genero || sec?.genero);
    accumulateProfile(idade, sec?.perfil_idade || sec?.idade);
    accumulateProfile(instrucao, sec?.perfil_instrucao || sec?.instrucao);

    Object.keys(sec || {}).forEach(campo => {
      if (!campo.startsWith('raiox_')) return;
      const cargo = campo.replace('raiox_', '');
      if (cargoKey && cargo !== cargoKey) return;
      const meta = getCargoMeta(cargo);
      const lista = sec[campo];
      if (!Array.isArray(lista)) return;

      lista.forEach(item => {
        const numero = String(item.numero);
        const votos = Number(item.votos || 0);
        const id = `${cargo}_${numero}`;
        if (!candidatos[id]) {
          const ref = getCandidateRef(candidatosRef, cargo, numero);
          const partido = resolveCandidateParty(ref, numero, partidosCatalog);
          const canonicalIdentity = getCandidateCanonicalIdentity(ref, {
            numero,
            cargo,
            cargoKey: cargo,
            nome: ref.nome || `Candidato ${numero}`,
            partido,
          });
          candidatos[id] = {
            id,
            numero,
            cargoKey: cargo,
            cargo: meta.singular,
            nome: ref.nome || `Candidato ${numero}`,
            officialId: getCandidateOfficialId(ref),
            canonicalIdentity,
            canonicalId: canonicalIdentity.id,
            canonicalConfidence: canonicalIdentity.nivel_confianca,
            votos: 0,
            partido,
            federacao: getCandidateFederation(ref),
            foto: getCandidatePhotoRef(ref),
          };
        }
        candidatos[id].votos += votos;
        totalVotosNominais += votos;
      });
    });
  });

  const officialAbstencoes = toOptionalNumber(officialCargoSummary?.abstencoes);
  if (officialAbstencoes !== null) {
    abstencoesDisponiveis = true;
    totalAbstencoes = officialAbstencoes;
    fontesMetadados.abstencoes = {
      fonte: `apuracao_oficial_por_cargo.${cargoKey}.abstencoes`,
      granularidade: 'municipio',
      usadoNoRecorte: true,
      regra: 'Usa o total oficial agregado do município para o cargo selecionado.',
    };
  }

  const officialBrancos = toOptionalNumber(officialCargoSummary?.votos_brancos);
  const officialNulos = toOptionalNumber(officialCargoSummary?.votos_nulos);
  if (officialBrancos !== null || officialNulos !== null) {
    brancosNulosDisponiveis = true;
    totalBrancos = officialBrancos || 0;
    totalNulos = officialNulos || 0;
  }

  const officialNominais = toOptionalNumber(officialCargoSummary?.votos_nominais);
  if (officialNominais !== null) totalVotosNominais = officialNominais;

  const officialLegenda = toOptionalNumber(officialCargoSummary?.votos_legenda);
  if (officialLegenda !== null) {
    votosLegendaDisponiveis = true;
    totalVotosLegenda = officialLegenda;
  }

  const officialValidos = toOptionalNumber(officialCargoSummary?.votos_validos);
  totalVotosValidos = officialValidos !== null
    ? officialValidos
    : totalVotosNominais + (votosLegendaDisponiveis ? totalVotosLegenda : 0);

  const candidatosArr = Object.values(candidatos)
    .map(candidate => ({
      ...candidate,
      percentual: totalVotosValidos > 0 ? (Number(candidate.votos || 0) / totalVotosValidos) * 100 : 0,
    }))
    .sort((a, b) => b.votos - a.votos);
  const eleitoresFonte = municipioTotalEleitores !== null ? 'municipio_total_aptos' : 'secoes';
  const eleitoresDivergencia = buildEleitoresDivergencia(municipioTotalEleitores, totalEleitoresSecoes);

  const aggregate = {
    totalEleitores,
    totalEleitoresSecoes,
    totalSecoes,
    totalVotosValidos,
    totalVotosNominais,
    totalVotosLegenda: votosLegendaDisponiveis ? totalVotosLegenda : null,
    votosLegendaDisponiveis,
    votosLegendaSecoesComDado,
    totalAbstencoes: abstencoesDisponiveis ? totalAbstencoes : null,
    abstencoesDisponiveis,
    abstencoesSecoesComDado,
    totalBrancos: brancosNulosDisponiveis ? totalBrancos : null,
    totalNulos: brancosNulosDisponiveis ? totalNulos : null,
    totalBrancosNulos: brancosNulosDisponiveis ? totalBrancos + totalNulos : null,
    brancosNulosDisponiveis,
    candidatos: candidatosArr,
    genero,
    idade,
    instrucao,
    cargoKey: cargoKey || null,
    eleitoresFonte,
    fontesMetadados,
    municipioTotalEleitores,
    eleitoresDivergencia,
    eleitoresMetadados: buildEleitoresMetadados({
      fontePrincipal: eleitoresFonte,
      totalMunicipal: municipioTotalEleitores,
      totalSecoes: totalEleitoresSecoes,
      totalEleitores,
      divergencia: eleitoresDivergencia,
      escopo: 'municipio',
    }),
  };
  aggregate.competitividade = buildCompetitivenessIndex(aggregate, {
    scope: 'municipio',
    partidosCatalog,
  });
  aggregate.territorios = buildTerritoryOpportunityData(dados, {
    cargoKey,
    scope: 'municipio',
    partidosCatalog,
  });
  return aggregate;
}

export function serverAggregateToPanelAggregate(serverAggregate, cargoKey) {
  const cargoData = serverAggregate?.por_cargo?.[cargoKey];
  if (!cargoData) return null;

  const totalizadores = cargoData.totalizadores || {};
  const disponibilidade = cargoData.disponibilidade || {};
  const fontesMetadados = normalizeServerFontesMetadados(cargoData.fontes_metadados || cargoData.fontesMetadados || {});
  const ranking = cargoData.ranking || {};
  const candidatos = Array.isArray(ranking.items) ? ranking.items.map(item => {
    const canonicalIdentity = item.identidade_canonica || item.canonicalIdentity || null;
    return {
      ...item,
      cargoKey: item.cargoKey || cargoKey,
      cargo: item.cargo || getCargoMeta(cargoKey).singular,
      officialId: item.officialId || item.seq || canonicalIdentity?.id_oficial_tse || '',
      canonicalIdentity,
      canonicalId: item.canonicalId || canonicalIdentity?.id || '',
      canonicalConfidence: item.canonicalConfidence || canonicalIdentity?.nivel_confianca || '',
      percentual: Number(item.percentual || 0),
      votos: Number(item.votos || 0),
    };
  }) : [];
  const rankingMeta = {
    total: Number(ranking.total || candidatos.length),
    page: Number(ranking.page || 1),
    pageSize: Number(ranking.page_size || ranking.pageSize || candidatos.length || 15),
    hasMore: Boolean(ranking.has_more || ranking.hasMore),
  };
  candidatos.rankingMeta = rankingMeta;

  const eleitoresDivergencia = buildEleitoresDivergencia(totalizadores.eleitores, totalizadores.eleitores_secoes);
  const aggregate = {
    totalEleitores: Number(totalizadores.eleitores || 0),
    totalEleitoresSecoes: Number(totalizadores.eleitores_secoes || totalizadores.eleitoresSecoes || totalizadores.eleitores || 0),
    totalSecoes: Number(totalizadores.secoes || 0),
    totalVotosValidos: Number(totalizadores.votos_validos || totalizadores.votosValidos || 0),
    totalVotosNominais: Number(totalizadores.votos_nominais || totalizadores.votosNominais || 0),
    totalVotosLegenda: totalizadores.votos_legenda ?? totalizadores.votosLegenda ?? null,
    votosLegendaDisponiveis: Boolean(disponibilidade.votos_legenda || disponibilidade.votosLegenda),
    votosLegendaSecoesComDado: 0,
    totalAbstencoes: disponibilidade.abstencoes ? Number(totalizadores.abstencoes || 0) : null,
    abstencoesDisponiveis: Boolean(disponibilidade.abstencoes),
    abstencoesSecoesComDado: 0,
    totalBrancos: disponibilidade.brancos_nulos ? Number(totalizadores.brancos || 0) : null,
    totalNulos: disponibilidade.brancos_nulos ? Number(totalizadores.nulos || 0) : null,
    totalBrancosNulos: disponibilidade.brancos_nulos ? Number(totalizadores.brancos_nulos || 0) : null,
    brancosNulosDisponiveis: Boolean(disponibilidade.brancos_nulos),
    candidatos,
    genero: cargoData.perfis?.genero || {},
    idade: cargoData.perfis?.idade || {},
    instrucao: cargoData.perfis?.instrucao || {},
    cargoKey,
    eleitoresFonte: serverAggregate?.escopo === 'zona' ? 'secoes' : 'server_aggregate',
    fontesMetadados,
    municipioTotalEleitores: totalizadores.eleitores ?? null,
    eleitoresDivergencia,
    eleitoresMetadados: buildEleitoresMetadados({
      fontePrincipal: serverAggregate?.escopo === 'zona' ? 'secoes' : 'server_aggregate',
      totalMunicipal: totalizadores.eleitores,
      totalSecoes: totalizadores.eleitores_secoes,
      totalEleitores: totalizadores.eleitores,
      divergencia: eleitoresDivergencia,
      escopo: serverAggregate?.escopo || 'municipio',
    }),
    rankingMeta,
    forcaPolitica: cargoData.forca_politica || null,
    territorios: normalizeTerritoryPayload(cargoData.territorios),
    serverAggregate: true,
  };
  aggregate.competitividade = normalizeCompetitivenessPayload(cargoData.competitividade)
    || buildCompetitivenessIndex(aggregate, {
      scope: serverAggregate?.escopo || 'municipio',
      rankingCompleto: !rankingMeta.hasMore,
    });
  return aggregate;
}

function normalizeServerFontesMetadados(metadata = {}) {
  if (!metadata || typeof metadata !== 'object') return {};
  return Object.fromEntries(Object.entries(metadata).map(([key, value]) => {
    const item = value && typeof value === 'object' ? value : {};
    return [key, {
      fonte: item.fonte || '',
      granularidade: item.granularidade || '',
      usadoNoRecorte: item.usadoNoRecorte ?? item.usado_no_recorte,
      regra: item.regra || '',
    }];
  }));
}

export function buildPoliticalStrengthData(agg, options = {}) {
  const candidatos = Array.isArray(agg?.candidatos) ? agg.candidatos : [];
  const totalVotos = Number(agg?.totalVotosValidos || 0);
  const partidosCatalog = options.partidosCatalog || DEFAULT_PARTY_CATALOG;

  return {
    totalVotos,
    partidos: aggregatePoliticalGroup(candidatos, 'partido', totalVotos, { ...options, partidosCatalog }),
    federacoes: aggregatePoliticalGroup(candidatos, 'federacao', totalVotos, options),
  };
}

export function buildCompetitivenessIndex(agg, options = {}) {
  const candidatos = Array.isArray(agg?.candidatos) ? agg.candidatos : [];
  const totalVotos = Number(agg?.totalVotosValidos || 0);
  const leader = candidatos[0] || null;
  const runnerUp = candidatos[1] || null;
  const factors = [];

  if (!leader || !runnerUp || totalVotos <= 0) {
    return {
      score: 0,
      level: 'indisponivel',
      label: 'Indisponível',
      scope: options.scope || 'municipio',
      confidence: 'baixa',
      totalVotes: totalVotos,
      marginVotes: null,
      marginPct: null,
      concentrationPct: null,
      leader,
      runnerUp,
      factors: [
        {
          key: 'margem_lideres',
          label: 'Margem entre líderes',
          score: null,
          available: false,
          detail: 'São necessários pelo menos dois candidatos com votos para calcular competitividade.',
        },
      ],
      unavailableFactors: buildUnavailableCompetitivenessFactors(options),
    };
  }

  const leaderVotes = Number(leader.votos || 0);
  const runnerUpVotes = Number(runnerUp.votos || 0);
  const marginVotes = Math.max(0, leaderVotes - runnerUpVotes);
  const marginPct = totalVotos > 0 ? (marginVotes / totalVotos) * 100 : 0;
  const marginScore = clampNumber(100 - (marginPct * 10), 0, 100);
  factors.push({
    key: 'margem_lideres',
    label: 'Margem entre líderes',
    score: roundNumber(marginScore),
    weight: 0.75,
    available: true,
    detail: `Diferença de ${marginVotes} voto(s), equivalente a ${roundNumber(marginPct, 2)}% do cargo selecionado.`,
  });

  const strength = agg?.forcaPolitica
    ? {
      partidos: Array.isArray(agg.forcaPolitica.partidos) ? agg.forcaPolitica.partidos : [],
    }
    : buildPoliticalStrengthData(agg, options);
  const dominantParty = strength.partidos[0] || null;
  const concentrationPct = Number(dominantParty?.percentual || 0);
  if (dominantParty) {
    const concentrationScore = clampNumber(100 - Math.max(0, concentrationPct - 50) * 2, 0, 100);
    factors.push({
      key: 'concentracao_partidaria',
      label: 'Concentração partidária',
      score: roundNumber(concentrationScore),
      weight: 0.25,
      available: true,
      detail: `${dominantParty.nome} concentra ${roundNumber(concentrationPct, 2)}% dos votos do cargo.`,
      party: dominantParty.nome,
      valuePct: roundNumber(concentrationPct, 2),
    });
  }

  if (options.previousAggregate) {
    const previous = buildCompetitivenessIndex(options.previousAggregate, {
      ...options,
      previousAggregate: null,
      historicalAggregates: null,
    });
    if (previous.marginPct !== null && previous.marginPct !== undefined) {
      const marginDelta = Number(previous.marginPct || 0) - marginPct;
      const winnerChanged = normalizeText(previous.leader?.canonicalId || previous.leader?.id || previous.leader?.nome)
        !== normalizeText(leader.canonicalId || leader.id || leader.nome);
      const temporalScore = clampNumber(50 + (marginDelta * 5) + (winnerChanged ? 20 : 0), 0, 100);
      factors.push({
        key: 'variacao_temporal',
        label: 'Variação temporal',
        score: roundNumber(temporalScore),
        weight: 0.15,
        available: true,
        detail: marginDelta >= 0
          ? `A disputa apertou ${roundNumber(Math.abs(marginDelta), 2)} p.p. contra a eleição comparada.`
          : `A disputa abriu ${roundNumber(Math.abs(marginDelta), 2)} p.p. contra a eleição comparada.`,
      });
    }
  }

  const historicalAggregates = Array.isArray(options.historicalAggregates) ? options.historicalAggregates : [];
  if (historicalAggregates.length) {
    const historicalMargins = historicalAggregates
      .map(item => buildCompetitivenessIndex(item, { ...options, previousAggregate: null, historicalAggregates: null }).marginPct)
      .filter(value => value !== null && value !== undefined);
    if (historicalMargins.length) {
      const averageMargin = historicalMargins.reduce((sum, value) => sum + Number(value || 0), 0) / historicalMargins.length;
      const historyScore = clampNumber(100 - averageMargin * 7, 0, 100);
      factors.push({
        key: 'historico',
        label: 'Histórico',
        score: roundNumber(historyScore),
        weight: 0.10,
        available: true,
        detail: `Margem histórica média de ${roundNumber(averageMargin, 2)} p.p. em ${historicalMargins.length} eleição(ões) comparáveis.`,
      });
    }
  }

  const availableFactors = factors.filter(factor => factor.available && Number.isFinite(Number(factor.score)));
  const totalWeight = availableFactors.reduce((sum, factor) => sum + Number(factor.weight || 0), 0) || availableFactors.length || 1;
  const weightedScore = availableFactors.reduce((sum, factor) => (
    sum + Number(factor.score || 0) * (Number(factor.weight || 0) || (totalWeight / availableFactors.length))
  ), 0) / totalWeight;
  const score = roundNumber(clampNumber(weightedScore, 0, 100));
  const level = getCompetitivenessLevel(score);

  return {
    score,
    level: level.key,
    label: level.label,
    scope: options.scope || 'municipio',
    confidence: options.rankingCompleto === false ? 'media' : 'alta',
    totalVotes: totalVotos,
    marginVotes,
    marginPct: roundNumber(marginPct, 2),
    concentrationPct: dominantParty ? roundNumber(concentrationPct, 2) : null,
    leader,
    runnerUp,
    dominantParty,
    factors,
    unavailableFactors: buildUnavailableCompetitivenessFactors(options, factors),
  };
}

export function buildSectionCompetitivenessData(dados, options = {}) {
  const cargoKey = normalizeCargoKey(options.cargoKey || '');
  if (!cargoKey) return [];

  const secoesObj = dados?.secoes || dados || {};
  const candidatosRef = dados?.candidatos || {};
  const zonaFilter = options.zona !== undefined && options.zona !== null && options.zona !== ''
    ? String(options.zona)
    : '';

  return Object.entries(secoesObj)
    .map(([key, secao]) => {
      if (!secao || typeof secao !== 'object') return null;
      const zona = String(secao.zona || extractZonaFromKey(key) || '');
      if (zonaFilter && zona !== zonaFilter) return null;
      const votosCargo = secao[`raiox_${cargoKey}`];
      if (!Array.isArray(votosCargo) || !votosCargo.length) return null;
      const aggregate = aggregateAll({
        candidatos: candidatosRef,
        secoes: { [key]: secao },
      }, {
        cargoKey,
        partidosCatalog: options.partidosCatalog,
      });
      const index = aggregate.competitividade || buildCompetitivenessIndex(aggregate, { scope: 'secao' });
      if (index.level === 'indisponivel') return null;
      return {
        ...index,
        scope: 'secao',
        key,
        zona,
        secao: String(secao.secao || extractSecaoFromKey(key) || key),
        totalVotes: aggregate.totalVotosValidos,
      };
    })
    .filter(Boolean)
    .sort((a, b) => (
      Number(b.score || 0) - Number(a.score || 0)
      || Number(a.marginVotes ?? Number.MAX_SAFE_INTEGER) - Number(b.marginVotes ?? Number.MAX_SAFE_INTEGER)
      || String(a.zona).localeCompare(String(b.zona), 'pt-BR', { numeric: true })
      || String(a.secao).localeCompare(String(b.secao), 'pt-BR', { numeric: true })
    ))
    .slice(0, Number(options.limit || 8));
}

export function buildTerritoryOpportunityData(dados, options = {}) {
  const cargoKey = normalizeCargoKey(options.cargoKey || '');
  const scope = options.scope || (options.zona ? 'zona' : 'municipio');
  const limit = Math.max(1, Number(options.limit || 8));
  if (!cargoKey) return emptyTerritoryOpportunityData(scope, cargoKey);

  const currentRows = buildTerritoryRows(dados, { ...options, cargoKey });
  const previousRows = options.previousDados
    ? buildTerritoryRows(options.previousDados, { ...options, cargoKey })
    : [];
  const redutosCandidatos = currentRows
    .filter(row => row.tipo === 'candidato' && row.classification === 'reduto')
    .sort(sortTerritoryStrengthRows)
    .slice(0, limit);
  const redutosPartidos = currentRows
    .filter(row => row.tipo === 'partido' && row.classification === 'reduto')
    .sort(sortTerritoryStrengthRows)
    .slice(0, limit);
  const oportunidadesCandidatos = currentRows
    .filter(row => row.tipo === 'candidato' && row.classification === 'oportunidade')
    .sort(sortTerritoryOpportunityRows)
    .slice(0, limit);
  const oportunidadesPartidos = currentRows
    .filter(row => row.tipo === 'partido' && row.classification === 'oportunidade')
    .sort(sortTerritoryOpportunityRows)
    .slice(0, limit);

  return {
    scope,
    cargoKey,
    totalSections: new Set(currentRows.map(row => row.key)).size,
    redutos: {
      candidatos: redutosCandidatos,
      partidos: redutosPartidos,
    },
    oportunidades: {
      candidatos: oportunidadesCandidatos,
      partidos: oportunidadesPartidos,
    },
    tendencias: buildTerritoryTrendData(currentRows, previousRows),
    metodo: {
      versao: 'territorios_v1',
      reduto: 'Lideranca local com percentual forte ou margem relevante no cargo selecionado.',
      oportunidade: 'Base local competitiva com poucos votos necessarios para assumir a lideranca.',
    },
  };
}

export function aggregateByZona(dados, zona, options = {}) {
  const secoesObj = dados?.secoes || dados || {};
  const filtered = {};
  Object.entries(secoesObj).forEach(([key, sec]) => {
    const secZona = sec?.zona || extractZonaFromKey(key);
    if (String(secZona) === String(zona)) {
      filtered[key] = sec;
    }
  });

  if (Object.keys(filtered).length > 0) {
    const agg = aggregateAll({ secoes: filtered, candidatos: dados?.candidatos || {} }, options);
    const cargoKey = options.cargoKey ? normalizeCargoKey(options.cargoKey) : null;
    const officialCargoSummary = cargoKey ? getOfficialCargoSummary(dados, cargoKey) : null;
    const officialAbstencoes = toOptionalNumber(officialCargoSummary?.abstencoes);
    if (officialAbstencoes !== null && !agg.abstencoesDisponiveis) {
      agg.fontesMetadados = {
        ...(agg.fontesMetadados || {}),
        abstencoes: {
          fonte: `apuracao_oficial_por_cargo.${cargoKey}.abstencoes`,
          granularidade: 'municipio',
          usadoNoRecorte: false,
          regra: 'Total oficial existe apenas no agregado municipal e nao e distribuido por zona.',
        },
      };
    }
    const municipioTotalEleitores = toOptionalNumber(dados?.municipio_total_aptos);
    const eleitoresDivergencia = buildEleitoresDivergencia(municipioTotalEleitores, agg.totalEleitores);
    const zoneAggregate = {
      ...agg,
      eleitoresFonte: 'secoes',
      municipioTotalEleitores,
      eleitoresDivergencia,
      eleitoresMetadados: buildEleitoresMetadados({
        fontePrincipal: 'secoes',
        totalMunicipal: municipioTotalEleitores,
        totalSecoes: agg.totalEleitores,
        totalEleitores: agg.totalEleitores,
        divergencia: eleitoresDivergencia,
        escopo: 'zona',
      }),
    };
    zoneAggregate.competitividade = buildCompetitivenessIndex(zoneAggregate, {
      scope: 'zona',
      partidosCatalog: options.partidosCatalog,
    });
    zoneAggregate.territorios = buildTerritoryOpportunityData({ secoes: filtered, candidatos: dados?.candidatos || {} }, {
      ...options,
      scope: 'zona',
      zona,
    });
    return zoneAggregate;
  }

  return aggregateAll(dados, options);
}

export function extractCargoKeys(dados) {
  const secoesObj = dados?.secoes || dados || {};
  const cargos = new Set();
  Object.values(secoesObj).forEach(sec => {
    Object.keys(sec || {}).forEach(key => {
      if (key.startsWith('raiox_') && Array.isArray(sec[key])) {
        cargos.add(key.replace('raiox_', ''));
      }
    });
  });
  return Array.from(cargos).sort((a, b) => getCargoMeta(a).label.localeCompare(getCargoMeta(b).label, 'pt-BR'));
}

export function extractZonas(dados) {
  const secoesObj = dados?.secoes || dados || {};
  const zonas = new Set();
  Object.entries(secoesObj).forEach(([key, sec]) => {
    const zona = sec?.zona || extractZonaFromKey(key);
    if (zona) zonas.add(String(zona));
  });
  return Array.from(zonas).sort((a, b) => Number(a) - Number(b));
}

export function getCandidateRef(candidatosRef = {}, cargoKey, numero) {
  const meta = getCargoMeta(cargoKey);
  // Chaves qualificadas usam singular com "_" (deputado_federal_1330); o rótulo de exibição tem espaço.
  const singular = String(meta.singular || '').trim().replace(/\s+/g, '_');
  return candidatosRef[`${singular}_${numero}`]
    || candidatosRef[`${cargoKey}_${numero}`]
    || candidatosRef[numero]
    || {};
}

export function getCandidateParty(ref = {}) {
  return ref.partido || ref.sigla_partido || ref.partido_sigla || '';
}

export function getCandidateFederation(ref = {}) {
  return ref.federacao || ref.federacao_nome || ref.partido_federacao || ref.coligacao || '';
}

export function getCandidatePhotoRef(ref = {}) {
  return ref.foto || ref.foto_url || ref.fotoUrl || ref.foto_path || ref.fotoPath || '';
}

export function getCandidateOfficialId(ref = {}) {
  return String(
    ref.id_candidato
    || ref.idCandidato
    || ref.sq_candidato
    || ref.sqCandidato
    || ref.sequencial_candidato
    || ref.sequencialCandidato
    || ref.sequencial
    || ref.seq
    || ref.officialId
    || ''
  ).trim();
}

export function getCandidateCanonicalIdentity(ref = {}, fallback = {}) {
  const raw = ref.identidade_canonica || ref.canonicalIdentity || ref.canonical_identity;
  if (raw && typeof raw === 'object') {
    return {
      ...raw,
      id: raw.id || raw.canonicalId || raw.canonical_id || '',
      id_oficial_tse: raw.id_oficial_tse || raw.officialId || raw.idOficialTse || getCandidateOfficialId(ref),
      nivel_confianca: raw.nivel_confianca || raw.confidence || raw.nivelConfianca || '',
      criterios: Array.isArray(raw.criterios) ? raw.criterios : (Array.isArray(raw.criteria) ? raw.criteria : []),
    };
  }

  const officialId = getCandidateOfficialId(ref) || fallback.officialId || '';
  const nome = ref.nome || fallback.nome || '';
  const cargo = ref.cargo || fallback.cargo || fallback.cargoKey || '';
  const numero = ref.numero || fallback.numero || '';
  const nomeNormalizado = normalizeCandidateName(nome);
  return {
    id: officialId ? `tse:${officialId}` : `nome:${normalizeText(`${cargo} ${numero} ${nomeNormalizado}`)}`,
    id_oficial_tse: officialId,
    nome_normalizado: nomeNormalizado,
    nome_urna: nome,
    partido: getCandidateParty(ref) || fallback.partido || '',
    cargo,
    ano: ref.ano || fallback.ano || '',
    municipio: ref.municipio || fallback.municipio || '',
    nivel_confianca: officialId ? 'alta' : 'media',
    criterios: [
      officialId ? 'identificador oficial' : '',
      nomeNormalizado ? 'nome normalizado' : '',
      cargo ? 'cargo' : '',
      getCandidateParty(ref) || fallback.partido ? 'partido' : '',
    ].filter(Boolean),
  };
}

export function buildRankingViewModel(candidatos = [], options = {}) {
  const limit = Math.max(1, Number(options.limit || 15));
  const page = Math.max(1, Number(options.page || 1));
  const visibleLimit = limit * page;
  const rows = Array.isArray(candidatos) ? candidatos : [];
  const visible = rows.slice(0, visibleLimit);
  const hiddenCount = Math.max(0, rows.length - visible.length);

  return {
    visible,
    total: rows.length,
    hiddenCount,
    truncated: hiddenCount > 0,
    hasMore: hiddenCount > 0,
    limit,
    page,
  };
}

export function buildCandidateListViewModel(candidatos = [], query = '', options = {}) {
  const limit = Math.max(1, Number(options.limit || 24));
  const page = Math.max(1, Number(options.page || 1));
  const queryNorm = normalizeText(query);
  const rows = Array.isArray(candidatos) ? candidatos : [];
  const filtered = rows.filter(candidate => !queryNorm || normalizeText(`${candidate.nome || ''} ${candidate.numero || ''} ${candidate.partido || ''}`).includes(queryNorm));
  const visibleLimit = limit * page;
  const items = filtered.slice(0, visibleLimit);
  const hiddenCount = Math.max(0, filtered.length - items.length);

  return {
    items,
    total: filtered.length,
    sourceTotal: rows.length,
    hiddenCount,
    truncated: hiddenCount > 0,
    hasMore: hiddenCount > 0,
    query: queryNorm,
    limit,
    page,
  };
}

const CARGO_PADRAO_PRIORIDADE = ['presidentes', 'governadores', 'senadores', 'prefeitos', 'vereadores'];

export function chooseDefaultCargoKey(cargos = [], preferred = '') {
  if (preferred && cargos.includes(preferred)) return preferred;
  return CARGO_PADRAO_PRIORIDADE.find(cargo => cargos.includes(cargo)) || cargos[0] || '';
}

export function normalizeCargoKey(cargoKey = '') {
  return String(cargoKey || '').replace(/^raiox_/, '').trim();
}

export function normalizeText(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

export function normalizeCandidateName(value) {
  return normalizeText(value).replace(/[^a-z0-9\s]/g, ' ').replace(/\s+/g, ' ').trim();
}

export function normalizeIdentityValue(value) {
  return String(value || '').replace(/\D/g, '') || String(value || '').trim().toLowerCase();
}

export function toOptionalNumber(value) {
  if (value === undefined || value === null || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function getOfficialCargoSummary(dados, cargoKey) {
  const key = normalizeCargoKey(cargoKey);
  const byCargo = dados?.apuracao_oficial_por_cargo
    || dados?.apuracaoOficialPorCargo
    || dados?.resumo_oficial_por_cargo
    || {};
  if (byCargo[key]) return byCargo[key];
  const meta = getCargoMeta(key);
  return byCargo[meta.singular]
    || byCargo[meta.plural?.toLowerCase?.()]
    || byCargo[meta.label?.toLowerCase?.()]
    || null;
}

export function getCargoLegendVotes(sec, cargoKey) {
  const cargo = normalizeCargoKey(cargoKey);
  return getOptionalNumberByAliases(sec, [
    `votos_legenda_${cargo}`,
    `votos_de_legenda_${cargo}`,
    `total_votos_legenda_${cargo}`,
    `total_votos_de_legenda_${cargo}`,
    'votos_legenda',
    'votos_de_legenda',
  ]);
}

export function extractZonaFromKey(key) {
  const parts = String(key).split('_');
  if (parts.length >= 2) return parts[0].replace(/^0+/, '') || parts[0];
  return null;
}

export function extractSecaoFromKey(key) {
  const parts = String(key).split('_');
  const raw = parts.length >= 2 ? parts[parts.length - 1] : key;
  return String(raw).replace(/^0+/, '') || String(raw);
}

export function getPoliticalPartyName(candidato = {}, partidosCatalog = null) {
  if (isNonNominalCandidate(candidato)) return FALLBACK_PARTY_LABEL;

  const explicitParty = getCandidateParty(candidato);
  if (explicitParty) return explicitParty;

  const prefix = getCandidatePartyPrefix(candidato.numero);
  const catalogParty = getPartyFromCatalog(partidosCatalog, prefix);
  if (catalogParty) return catalogParty;

  return prefix ? `Partido ${prefix}` : FALLBACK_PARTY_LABEL;
}

export function normalizePoliticalLabel(value, fallback) {
  const normalized = String(value || '').trim();
  return normalized || fallback;
}

function accumulateProfile(target, source) {
  if (Array.isArray(source)) {
    source.forEach(item => {
      const key = item.categoria || item.genero || item.idade || item.instrucao;
      const value = item.quantidade || 0;
      if (target[key] !== undefined) target[key] += value;
    });
  } else if (source && typeof source === 'object') {
    Object.entries(source).forEach(([key, value]) => {
      if (target[key] !== undefined) target[key] += value;
    });
  }
}

function normalizeCompetitivenessPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const normalized = {
    ...payload,
    score: Number(payload.score || 0),
    level: payload.level || payload.nivel || 'indisponivel',
    label: payload.label || payload.rotulo || 'Indisponível',
    scope: payload.scope || payload.escopo || 'municipio',
    confidence: payload.confidence || payload.confianca || 'media',
    totalVotes: Number(payload.totalVotes ?? payload.total_votos ?? 0),
    marginVotes: payload.marginVotes ?? payload.margem_votos ?? null,
    marginPct: payload.marginPct ?? payload.margem_percentual ?? null,
    concentrationPct: payload.concentrationPct ?? payload.concentracao_percentual ?? null,
    leader: payload.leader || payload.lider || null,
    runnerUp: payload.runnerUp || payload.segundo || null,
    dominantParty: payload.dominantParty || payload.partido_dominante || null,
    factors: Array.isArray(payload.factors) ? payload.factors : (Array.isArray(payload.fatores) ? payload.fatores : []),
    unavailableFactors: Array.isArray(payload.unavailableFactors)
      ? payload.unavailableFactors
      : (Array.isArray(payload.fatores_indisponiveis) ? payload.fatores_indisponiveis : []),
    secoesDecisivas: Array.isArray(payload.secoesDecisivas)
      ? payload.secoesDecisivas
      : (Array.isArray(payload.secoes_decisivas) ? payload.secoes_decisivas : []),
  };
  return normalized;
}

function normalizeTerritoryPayload(payload) {
  if (!payload || typeof payload !== 'object') return null;
  const redutos = payload.redutos || {};
  const oportunidades = payload.oportunidades || {};
  const tendencias = payload.tendencias || {};
  return {
    ...payload,
    scope: payload.scope || payload.escopo || 'municipio',
    cargoKey: payload.cargoKey || payload.cargo_key || '',
    totalSections: Number(payload.totalSections ?? payload.total_sections ?? 0),
    redutos: {
      candidatos: Array.isArray(redutos.candidatos) ? redutos.candidatos : [],
      partidos: Array.isArray(redutos.partidos) ? redutos.partidos : [],
    },
    oportunidades: {
      candidatos: Array.isArray(oportunidades.candidatos) ? oportunidades.candidatos : [],
      partidos: Array.isArray(oportunidades.partidos) ? oportunidades.partidos : [],
    },
    tendencias: {
      available: Boolean(tendencias.available),
      crescimento: Array.isArray(tendencias.crescimento) ? tendencias.crescimento : [],
      queda: Array.isArray(tendencias.queda) ? tendencias.queda : [],
      note: tendencias.note || tendencias.nota || '',
    },
    metodo: payload.metodo || {},
  };
}

function emptyTerritoryOpportunityData(scope, cargoKey) {
  return {
    scope,
    cargoKey,
    totalSections: 0,
    redutos: { candidatos: [], partidos: [] },
    oportunidades: { candidatos: [], partidos: [] },
    tendencias: {
      available: false,
      crescimento: [],
      queda: [],
      note: 'Informe uma eleição anterior comparável para detectar crescimento ou queda territorial.',
    },
    metodo: {
      versao: 'territorios_v1',
      reduto: 'Indisponível sem cargo selecionado.',
      oportunidade: 'Indisponível sem cargo selecionado.',
    },
  };
}

function buildTerritoryRows(dados, options = {}) {
  const cargoKey = normalizeCargoKey(options.cargoKey || '');
  if (!cargoKey) return [];
  const secoesObj = dados?.secoes || dados || {};
  const candidatosRef = dados?.candidatos || {};
  const partidosCatalog = options.partidosCatalog || DEFAULT_PARTY_CATALOG;
  const zonaFilter = options.zona !== undefined && options.zona !== null && options.zona !== ''
    ? String(options.zona)
    : '';
  const rows = [];

  Object.entries(secoesObj).forEach(([key, secao]) => {
    if (!secao || typeof secao !== 'object') return;
    const zona = String(secao.zona || extractZonaFromKey(key) || '');
    if (zonaFilter && zona !== zonaFilter) return;
    const votosCargo = secao[`raiox_${cargoKey}`];
    if (!Array.isArray(votosCargo) || !votosCargo.length) return;
    const totalVotes = votosCargo.reduce((sum, item) => sum + Number(item?.votos || 0), 0);
    if (totalVotes <= 0) return;
    const sortedCandidates = votosCargo
      .map(item => {
        const numero = String(item?.numero || '').trim();
        if (!numero) return null;
        const votos = Number(item?.votos || 0);
        const ref = getCandidateRef(candidatosRef, cargoKey, numero);
        const partido = resolveCandidateParty(ref, numero, partidosCatalog);
        return {
          id: `${cargoKey}_${numero}`,
          numero,
          nome: ref.nome || `Candidato ${numero}`,
          partido,
          votos,
        };
      })
      .filter(item => item && item.votos > 0)
      .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));

    rows.push(...buildEntityTerritoryRows({
      type: 'candidato',
      entities: sortedCandidates,
      totalVotes,
      key,
      zona,
      secao: String(secao.secao || extractSecaoFromKey(key) || key),
    }));

    const partyRows = aggregateSectionParties(sortedCandidates, totalVotes);
    rows.push(...buildEntityTerritoryRows({
      type: 'partido',
      entities: partyRows,
      totalVotes,
      key,
      zona,
      secao: String(secao.secao || extractSecaoFromKey(key) || key),
    }));
  });

  return rows;
}

function aggregateSectionParties(candidates, totalVotes) {
  const groups = new Map();
  candidates.forEach(candidate => {
    const label = normalizePoliticalLabel(candidate.partido, FALLBACK_PARTY_LABEL);
    const groupKey = normalizeText(label);
    if (!groups.has(groupKey)) {
      groups.set(groupKey, {
        id: `partido_${groupKey}`,
        nome: label,
        votos: 0,
        candidatos: 0,
      });
    }
    const group = groups.get(groupKey);
    group.votos += Number(candidate.votos || 0);
    group.candidatos += 1;
  });

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      percentual: totalVotes > 0 ? (group.votos / totalVotes) * 100 : 0,
    }))
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));
}

function buildEntityTerritoryRows({ type, entities, totalVotes, key, zona, secao }) {
  if (!Array.isArray(entities) || entities.length < 2) return [];
  const leader = entities[0];
  const runnerUp = entities[1];
  const leaderVotes = Number(leader.votos || 0);
  const runnerVotes = Number(runnerUp.votos || 0);
  const rows = [];

  entities.forEach((entity, index) => {
    const votes = Number(entity.votos || 0);
    const percentual = totalVotes > 0 ? (votes / totalVotes) * 100 : 0;
    const rank = index + 1;
    const gapToLeader = Math.max(0, leaderVotes - votes);
    const marginVotes = rank === 1 ? Math.max(0, votes - runnerVotes) : gapToLeader;
    const marginPct = totalVotes > 0 ? (marginVotes / totalVotes) * 100 : 0;
    const votesToLead = rank === 1 ? 0 : gapToLeader + 1;
    const reduto = rank === 1 && (percentual >= 55 || marginPct >= 15);
    const opportunityGapLimit = Math.max(5, Math.ceil(totalVotes * 0.08));
    const oportunidade = rank > 1 && rank <= 3 && (
      votesToLead <= opportunityGapLimit || percentual >= 35
    );

    if (!reduto && !oportunidade) return;

    const score = reduto
      ? roundNumber(clampNumber(percentual + (marginPct * 0.4), 0, 100))
      : roundNumber(clampNumber(100 - (marginPct * 8) + Math.min(percentual, 45) * 0.25, 0, 100));
    rows.push({
      tipo: type,
      classification: reduto ? 'reduto' : 'oportunidade',
      key,
      zona,
      secao,
      id: entity.id || '',
      numero: entity.numero || '',
      nome: entity.nome || '',
      partido: entity.partido || '',
      votos: votes,
      totalVotes,
      percentual: roundNumber(percentual, 2),
      rank,
      marginVotes,
      marginPct: roundNumber(marginPct, 2),
      gapToLeader,
      votesToLead,
      score,
      leader: {
        id: leader.id || '',
        numero: leader.numero || '',
        nome: leader.nome || '',
        partido: leader.partido || '',
        votos: leaderVotes,
      },
      runnerUp: {
        id: runnerUp.id || '',
        numero: runnerUp.numero || '',
        nome: runnerUp.nome || '',
        partido: runnerUp.partido || '',
        votos: runnerVotes,
      },
    });
  });

  return rows;
}

function buildTerritoryTrendData(currentRows, previousRows) {
  if (!Array.isArray(previousRows) || !previousRows.length) {
    return {
      available: false,
      crescimento: [],
      queda: [],
      note: 'Informe uma eleição anterior comparável para detectar crescimento ou queda territorial.',
    };
  }

  const previousMap = new Map(previousRows.map(row => [territoryTrendKey(row), row]));
  const trends = currentRows
    .map(row => {
      const previous = previousMap.get(territoryTrendKey(row));
      if (!previous) return null;
      const deltaPct = Number(row.percentual || 0) - Number(previous.percentual || 0);
      return {
        ...row,
        previousPercentual: roundNumber(previous.percentual, 2),
        deltaPct: roundNumber(deltaPct, 2),
      };
    })
    .filter(Boolean)
    .filter(row => Math.abs(Number(row.deltaPct || 0)) >= 3);

  return {
    available: true,
    crescimento: trends
      .filter(row => Number(row.deltaPct || 0) > 0)
      .sort((a, b) => Number(b.deltaPct || 0) - Number(a.deltaPct || 0))
      .slice(0, 6),
    queda: trends
      .filter(row => Number(row.deltaPct || 0) < 0)
      .sort((a, b) => Number(a.deltaPct || 0) - Number(b.deltaPct || 0))
      .slice(0, 6),
    note: 'Tendência calculada contra eleição anterior comparável no mesmo cargo e seção.',
  };
}

function territoryTrendKey(row) {
  const entity = row.tipo === 'partido'
    ? normalizeText(row.nome)
    : normalizeText(row.id || row.numero || row.nome);
  return `${row.tipo}:${row.key}:${entity}`;
}

function sortTerritoryStrengthRows(a, b) {
  return Number(b.score || 0) - Number(a.score || 0)
    || Number(b.percentual || 0) - Number(a.percentual || 0)
    || Number(b.votos || 0) - Number(a.votos || 0)
    || String(a.zona).localeCompare(String(b.zona), 'pt-BR', { numeric: true })
    || String(a.secao).localeCompare(String(b.secao), 'pt-BR', { numeric: true });
}

function sortTerritoryOpportunityRows(a, b) {
  return Number(a.votesToLead || 0) - Number(b.votesToLead || 0)
    || Number(b.score || 0) - Number(a.score || 0)
    || Number(b.percentual || 0) - Number(a.percentual || 0)
    || String(a.zona).localeCompare(String(b.zona), 'pt-BR', { numeric: true })
    || String(a.secao).localeCompare(String(b.secao), 'pt-BR', { numeric: true });
}

function buildUnavailableCompetitivenessFactors(options = {}, factors = []) {
  const availableKeys = new Set(factors.filter(factor => factor.available).map(factor => factor.key));
  const unavailable = [];
  if (!options.previousAggregate && !availableKeys.has('variacao_temporal')) {
    unavailable.push({
      key: 'variacao_temporal',
      label: 'Variação temporal',
      available: false,
      detail: 'Compare com outra eleição equivalente para incluir evolução temporal no score.',
    });
  }
  if ((!Array.isArray(options.historicalAggregates) || !options.historicalAggregates.length) && !availableKeys.has('historico')) {
    unavailable.push({
      key: 'historico',
      label: 'Histórico',
      available: false,
      detail: 'Histórico entra no índice quando houver eleições anteriores comparáveis no mesmo cargo e recorte.',
    });
  }
  return unavailable;
}

function getCompetitivenessLevel(score) {
  const value = Number(score || 0);
  if (value >= 80) return { key: 'muito_alta', label: 'Muito alta' };
  if (value >= 60) return { key: 'alta', label: 'Alta' };
  if (value >= 35) return { key: 'media', label: 'Média' };
  if (value > 0) return { key: 'baixa', label: 'Baixa' };
  return { key: 'indisponivel', label: 'Indisponível' };
}

function clampNumber(value, min, max) {
  const number = Number(value || 0);
  return Math.min(max, Math.max(min, number));
}

function roundNumber(value, digits = 0) {
  const factor = 10 ** digits;
  return Math.round(Number(value || 0) * factor) / factor;
}

function aggregatePoliticalGroup(candidatos, type, totalVotos, options = {}) {
  const groups = new Map();
  const fallback = type === 'partido' ? FALLBACK_PARTY_LABEL : FALLBACK_FEDERATION_LABEL;

  candidatos.forEach(candidato => {
    const votos = Number(candidato?.votos || 0);
    if (!Number.isFinite(votos) || votos <= 0) return;

    const nome = type === 'partido'
      ? getPoliticalPartyName(candidato, options.partidosCatalog)
      : getPoliticalFederationName(candidato);
    const label = normalizePoliticalLabel(nome, fallback);
    const key = normalizeText(label);

    if (!groups.has(key)) {
      groups.set(key, {
        nome: label,
        votos: 0,
        candidatos: 0,
        percentual: 0,
      });
    }

    const group = groups.get(key);
    group.votos += votos;
    group.candidatos += 1;
  });

  return Array.from(groups.values())
    .map(group => ({
      ...group,
      percentual: totalVotos > 0 ? (group.votos / totalVotos) * 100 : 0,
    }))
    .sort((a, b) => b.votos - a.votos || a.nome.localeCompare(b.nome, 'pt-BR'));
}

function resolveCandidateParty(ref = {}, numero = '', partidosCatalog = null) {
  const explicitParty = getCandidateParty(ref);
  if (explicitParty) return explicitParty;

  const prefix = getCandidatePartyPrefix(numero);
  const catalogParty = getPartyFromCatalog(partidosCatalog, prefix);
  if (catalogParty) return catalogParty;

  return '';
}

function getPoliticalFederationName(candidato = {}) {
  return getCandidateFederation(candidato) || FALLBACK_FEDERATION_LABEL;
}

function getCandidatePartyPrefix(numero) {
  const clean = String(numero || '').replace(/\D/g, '');
  if (clean.length >= 2) return clean.slice(0, 2);
  return '';
}

function getPartyFromCatalog(partidosCatalog, prefix) {
  if (!partidosCatalog || !prefix) return '';
  const entry = partidosCatalog[String(prefix)];
  if (!entry || typeof entry !== 'object') return '';
  return entry.sigla || entry.nome || '';
}

function isNonNominalCandidate(candidato = {}) {
  const text = normalizeText(`${candidato.nome || ''} ${candidato.numero || ''}`);
  return /\b(branco|nulo|anulado)\b/.test(text);
}

function getOptionalNumberByAliases(source, aliases = []) {
  for (const alias of aliases) {
    const value = toOptionalNumber(source?.[alias]);
    if (value !== null) return value;
  }
  return null;
}

function getCargoScopedOptionalNumber(sec, cargoKey, aliases = []) {
  const cargo = normalizeCargoKey(cargoKey);
  const scopedAliases = aliases.flatMap(alias => [
    `${alias}_${cargo}`,
    `${alias}_do_${cargo}`,
    `${alias}_de_${cargo}`,
  ]);
  return getOptionalNumberByAliases(sec, [...scopedAliases, ...aliases]);
}

function buildEleitoresDivergencia(referencia, comparado) {
  const ref = toOptionalNumber(referencia);
  const comp = toOptionalNumber(comparado);
  if (ref === null || comp === null || ref === comp) return null;

  const valor = Math.abs(ref - comp);
  const percentual = ref > 0 ? Number(((valor / ref) * 100).toFixed(2)) : 0;
  return {
    valor,
    percentual,
    severidade: getDivergenceSeverity(percentual),
    referencia: ref,
    comparado: comp,
  };
}

function getDivergenceSeverity(percentual) {
  const value = Number(percentual || 0);
  if (value >= 5) return 'alta';
  if (value >= 1) return 'media';
  if (value > 0) return 'baixa';
  return 'ok';
}

function buildEleitoresMetadados({ fontePrincipal, totalMunicipal, totalSecoes, totalEleitores, divergencia, escopo }) {
  const mainSource = fontePrincipal || (totalMunicipal !== null && totalMunicipal !== undefined ? 'municipio_total_aptos' : 'secoes');
  const rule = mainSource === 'municipio_total_aptos'
    ? 'Usa municipio_total_aptos como total principal e compara com a soma de secao.aptos.'
    : 'Usa a soma de secao.aptos como total principal porque municipio_total_aptos nao esta disponivel.';

  return {
    escopo: escopo || 'municipio',
    fontePrincipal: mainSource,
    fonteSecoes: 'secao.aptos',
    regraCalculo: rule,
    totalMunicipal: totalMunicipal ?? null,
    totalSecoes: Number(totalSecoes || 0),
    totalEleitores: Number(totalEleitores || 0),
    severidade: divergencia?.severidade || 'ok',
  };
}
